import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { DiaryApi } from 'api/diary/diary-api.js';
import { DiaryDb } from 'lib/cosmos/diary/diaryDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

type ExtendedDiaryItem = {
	readonly item: DiaryDb.IDiaryItem;
	readonly date: string;
	readonly fromNow: string;
	readonly buttons: IButton[];
};

type DiaryFormItem = {
	readonly text: string;
};

@Component({
	templateUrl: './view-user-diary.component.html',
	selector: 'mod-view-user-diary',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ViewUserDiaryComponent extends TypeCoreComponent<UserDb.IUserItem> {
	public readonly diaryItems = signal<ExtendedDiaryItem[] | undefined>(undefined);
	public readonly diaryText = signal<string>('');

	public saveHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		const diaryText = this.diaryText();
		if (!diaryText) {
			return undefined;
		}

		return this.dependencies.httpService
			.post<DiaryDb.IDiaryItem, DiaryApi.IPostDiaryEntryRequest>((builder) =>
				builder
					.withCustomApiRoute('diary', 'diary-entry')
					.withData({
						text: diaryText,
						userId: sharedData.data.id,
						diaryId: undefined
					})
					.build()
			)
			.pipe(
				map((response) => {
					super.notify(`Diary updated`);

					this.diaryItems.update((oldValue) => {
						oldValue?.unshift(this.mapToExtendedDiaryItems(response.data));

						return [...(oldValue ?? [])];
					});

					this.diaryText.set('');
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.initDiary(sharedData.data);
		};
	}

	private mapToExtendedDiaryItems(diaryItem: DiaryDb.IDiaryItem): ExtendedDiaryItem {
		const extended: ExtendedDiaryItem = {
			date: super.formatDate(diaryItem.system.created),
			item: diaryItem,
			fromNow: super.fromNow(diaryItem.system.created),
			buttons: [
				{
					iconOnly: true,
					title: `Edit record`,
					icon: 'edit',
					buttonType: 'basic',
					click: () => {
						this.openEditDiaryItemDialog(diaryItem);
					}
				},
				{
					iconOnly: true,
					title: `Delete record`,
					icon: 'delete',
					buttonType: 'basic',
					confirm: {
						confirmMode: 'delete',
						text: `Are you sure to delete this diary record?`
					},
					click: () => {
						this.deleteDiaryRecord(diaryItem);
					}
				}
			]
		};

		return extended;
	}

	private initDiary(user: UserDb.IUserItem): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<DiaryDb.IDiaryItem[]>((builder) =>
					builder.withCustomApiRoute('diary', 'getDiaryForUser').withId(user.id).build()
				)
				.pipe(
					map((response) => {
						this.diaryItems.set(
							response.data
								.sort((a, b) => {
									return new Date(b.system.created).valueOf() - new Date(a.system.created).valueOf();
								})
								.map((m) => this.mapToExtendedDiaryItems(m))
						);
					})
				)
		);
	}

	private openEditDiaryItemDialog(diaryItem: DiaryDb.IDiaryItem): void {
		this.dependencies.dialogService.openFormDialog<DiaryFormItem>({
			title: 'Edit diary record',
			size: 'default',
			subtitle: 'Use diary as a training log, diet log or just to keep track of the progress',
			fields: [
				{
					title: undefined,
					type: 'textarea',
					propertyName: 'text',
					required: true
				}
			],
			item: {
				text: diaryItem.text
			},
			save: (item) => {
				super.subscribeToObservable(
					this.dependencies.httpService
						.post<DiaryDb.IDiaryItem, DiaryApi.IPostDiaryEntryRequest>((builder) =>
							builder
								.withCustomApiRoute('diary', 'diary-entry')
								.withData({
									text: item.text,
									userId: diaryItem.userId,
									diaryId: diaryItem.id
								})
								.build()
						)
						.pipe(
							map((response) => {
								super.notify(`Diary updated`);

								this.diaryItems.update((oldValue) => {
									if (oldValue) {
										const indexOf = oldValue.findIndex((m) => m.item.id === diaryItem.id);

										oldValue.splice(indexOf, 1, this.mapToExtendedDiaryItems(response.data));
									}

									return [...(oldValue ?? [])];
								});
							})
						)
				);
			}
		});
	}

	private deleteDiaryRecord(diaryItem: DiaryDb.IDiaryItem): void {
		super.subscribeToObservable(
			this.dependencies.httpService.deleteSingle('diary', diaryItem.id).pipe(
				map((response) => {
					super.notifyDeleted('Diary');

					this.diaryItems.update((oldValue) => {
						if (oldValue) {
							const indexOf = oldValue.findIndex((m) => m.item.id === diaryItem.id);
							oldValue.splice(indexOf, 1);
						}

						return [...(oldValue ?? [])];
					});
				})
			)
		);
	}
}
